import { Component, OnDestroy, OnInit } from '@angular/core';
import { ProjectRouteStation } from "../shared/interfaces/project-route-station.interface";
import { ProjectDataService } from "../shared/services/project-data.service";
import { ProjectImageData } from "../shared/interfaces/project-image-data.interface";
import { ImageDataService } from "../shared/services/image-data.service";
import { ModalService } from "../../shared/modal/shared/modal.service";
import { ProjectService } from "../shared/services/project.service";
import { Project } from "../shared/interfaces/project.interface";
import { AlertService } from "../../shared/alert/shared/alert.service";
import { AlertTypes } from "../../shared/alert/shared/alert-types";
import { SelectInput } from "../../shared/dropdown/shared/select-input.class";
import { MatDialog } from "@angular/material/dialog";
import { SetImageModalComponent } from "./set-image-modal/set-image-modal.component";
import { ImageService } from "../shared/services/image.service";
import { environment } from "../../../environments/environment";
import { Subscription, combineLatest } from 'rxjs';
import { AnimatedImageModalComponent } from '../project-settings/project-settings/animated-image-modal/animated-image-modal.component';
import { RouteStationService } from '../shared/services/route-station.service';
import { AnimatedImageErrorMessage } from './shared/animated-image-error-message';
import {
  ProjectRouteStationCombinedStellcodeStatus,
  ProjectRouteStationEngineParameterStellcodeStatus,
  ProjectRouteStationStellcodeStatus
} from '../shared/interfaces/project-route-station-stellcode-status.interface';
import { ImageUploadJob } from '../shared/interfaces/image-upload-job.interface';
import { DataEndChannelTypeEnum } from '../shared/enums/data-end-channel-type.enum';

@Component({
  selector: 'avs-project-images',
  templateUrl: './project-images.component.html',
  styleUrls: ['./project-images.component.css']
})
export class ProjectImagesComponent implements OnInit, OnDestroy {
  public routeStation: ProjectRouteStation | undefined;
  public unassignedImages: ProjectImageData[] = [];
  public stationImages: ProjectImageData[] = [];
  private activeProject: Project | undefined;
  protected readonly environment = environment;
  public subscriptionCollection: Subscription[] = [];
  private stationAndProjectSubscription?: Subscription;
  public unassignedImagesSubscription?: Subscription;
  public stationImagesSubscription?: Subscription;
  public dataEndChannelsSubscription?: Subscription;
  public openImageId: number = 0;
  public filteredImages: ProjectImageData[] = [];
  public assignedImages: ProjectImageData[] | undefined;
  public isSyncStellcodeOpen: boolean = false;
  public selectedSyncStellcode: SelectInput = { name: 'Kanal', id: 0 };
  public channelSyncStellcodeSettings: SelectInput[] = [];
  public isUploadJobActive: boolean = false;
  private engineParameterStellcodeStatuses: ProjectRouteStationEngineParameterStellcodeStatus[] = [];
  private scenarioStellcodeStatuses: ProjectRouteStationEngineParameterStellcodeStatus[] = [];
  private timerSwitchStellcodeStatuses: ProjectRouteStationEngineParameterStellcodeStatus[] = [];
  private fallbackStellcodeStatuses: ProjectRouteStationEngineParameterStellcodeStatus[] = [];
  private uploadJobPollingTimeout?: ReturnType<typeof setTimeout>;
  private imageRotationIntervals: ReturnType<typeof setInterval>[] = [];
  private readonly activeJobStatuses = [0, 1, 2, 3];

  constructor(private projectDataService: ProjectDataService,
    private imageDataService: ImageDataService,
    private modalService: ModalService,
    private projectService: ProjectService,
    private alertService: AlertService,
    private dialog: MatDialog,
    private imageService: ImageService,
    private routeStationService: RouteStationService) {
    this.stationAndProjectSubscription = combineLatest([
      projectDataService.activeRouteStation,
      projectDataService.activeProject
    ]).subscribe(([station, project]) => {
      this.clearUploadJobPolling();
      this.clearImageRotationIntervals();
      this.routeStation = station;
      this.activeProject = project;
      this.engineParameterStellcodeStatuses = [];
      this.scenarioStellcodeStatuses = [];
      this.timerSwitchStellcodeStatuses = [];
      this.fallbackStellcodeStatuses = [];
      this.getImagesForStation();
    });
    this.unassignedImagesSubscription = imageDataService.projectRouteStationUnassignedImages.subscribe(images => {
      this.unassignedImages = images ?? [];
    });
    this.stationImagesSubscription = imageDataService.projectRouteStationImages.subscribe(images => {
      this.clearImageRotationIntervals();
      this.stationImages = images ?? [];
      this.assignedImages = this.stationImages;
      if (this.stationImages.length > 0)
        this.rotateImagesPaths(this.stationImages);/* TODO: kann vermutlich entfernt werden */
    });
    this.dataEndChannelsSubscription = projectDataService.dataEndChannels.subscribe(endChannels => {
      this.channelSyncStellcodeSettings = endChannels
        .filter(c => c.eakanalnr !== 0 && c.dataEndChannelType === DataEndChannelTypeEnum.LED)
        .map(c => ({ name: `Kanal ${c.de}`, id: c.de }));
      this.ensureSyncChannelSelection();
    });
  }

  public ngOnInit(): void {
    this.subscriptionCollection.push(this.stationAndProjectSubscription!);
    this.subscriptionCollection.push(this.unassignedImagesSubscription!);
    this.subscriptionCollection.push(this.stationImagesSubscription!);
    this.subscriptionCollection.push(this.dataEndChannelsSubscription!);
  }
  public ngOnDestroy(): void {
    this.clearUploadJobPolling();
    this.clearImageRotationIntervals();
    this.subscriptionCollection.forEach(sub => sub.unsubscribe());
  }



  public onGenerateAnimatedImageClicked(): void {
    this.filterNextOpenImageId();

    const dialogRef = this.dialog.open(AnimatedImageModalComponent, { data: { data: this.activeProject, openImageId: this.openImageId, notAssignedImages: this.filteredImages, projectImages: this.unassignedImages, assignedImages: this.assignedImages, singleRouteStation: true } });
    dialogRef.afterClosed().subscribe(result => {
      if (result === undefined) return;
      this.routeStationService.generateAnimatedImageForRouteStation(this.routeStation?.id!, result.animatedImages, result.stellcode).subscribe((errorMessages: AnimatedImageErrorMessage[]) => {
        this.alertService.alert('Die Animation wurde erfolgreich erstellt', AlertTypes.success);
      },
        (error) => { this.alertService.alert('Die Animation konnte nicht erstellt werden', AlertTypes.error); }
      )
    }
    );
  }


  private filterNextOpenImageId() {
    this.filteredImages = [];
    this.openImageId = 0;
    if (this.unassignedImages) {
      this.unassignedImages.forEach(image => { if (image.imageId > 121) { this.filteredImages.push(image) } })
    }
    this.openImageId = Math.min.apply(Math, this.filteredImages.map(o => o.imageId));
  }
  public rotateImagesPaths(images: ProjectImageData[]): void {
    images.forEach(image => {
      if (image.isAnimated && image.animatedImageIds!.length > 0) { /* für jedes Bild, das für die RouteStation gesetzt wird, wird nun separat geprüft ob ein animatedImageId vorhanden ist */
        this.rotatePathForId(image)
      }
    });
  }

  public rotatePathForId(image: ProjectImageData): void {
    const myValueList = image.animatedImageIds!;

    let i = 0;
    const id = setInterval(() => {
      image.path = this.getPathByImageId(myValueList[i++]); /* iteration durchs animatedimage array, und zieht sich dasnn über die id, den path */
      if (i === myValueList.length) {
        i = 0;
      }
    }, 1000);
    this.imageRotationIntervals.push(id);
  }

  public getPathByImageId(imageId: number) {

    if (this.stationImages) {
      let imageObject = this.stationImages.find((x) => x.imageId == imageId)
      return imageObject?.path as string
    }
    else return
  }

  public onUnassignedProjectImageClicked(image: ProjectImageData): void {
    this.modalService.openDialog('Bild für Station setzen?', 'Abbrechen', 'Bestätigen', image).subscribe(
      () => {
        if (!this.activeProject || !this.routeStation) { return; }
        this.projectService.setImageForRouteStation(this.activeProject.id, this.routeStation.id, image.imageId).subscribe(() => {
          this.alertService.alert('Das Bild wurde zur Station hinzugefügt', AlertTypes.success);
          this.getImagesForStation();
        });
      }
    );
  }

  public onProjectImageClicked(image: ProjectImageData): void {
    this.dialog.open(SetImageModalComponent, { data: image }).afterClosed().subscribe(result => {
      if (result) {
        this.getImagesForStation();
      }
    });
  }

  public genericMethod(name: string): void {
    if (name === 'onGenerateAnimatedImageClicked') {
      this.onGenerateAnimatedImageClicked();
    }
  }

  public onChannelDropdownSyncStellcodeValueChange(selectItem: SelectInput): void {
    this.selectedSyncStellcode = selectItem;
    this.isSyncStellcodeOpen = false;
  }

  public onSyncStellcodeBtnClicked(): void {
    if (!this.routeStation || this.selectedSyncStellcode.id === 0 || this.areSyncChannelActionsDisabled) { return; }
    this.imageService.syncStellcodes(this.selectedSyncStellcode.id, this.routeStation.samId).subscribe(() => {
      this.alertService.alert("Synchronisierung wurde angestoßen; dies kann einige Zeit in Anspruch nehmen", AlertTypes.info);
    });
  }

  public onUploadImagesClicked(): void {
    if (!this.routeStation || !this.activeProject || this.isUploadJobActive) { return; }
    this.imageService.startImageUploadJob({
      routeStationId: this.routeStation.id,
      projectId: this.activeProject.id
    }).subscribe(() => {
      this.alertService.alert("Der Upload-Job wurde gestartet.", AlertTypes.info);
      this.isUploadJobActive = true;
      this.applyStellcodeStatuses([]);
      this.applyEngineParameterStellcodeStatuses([]);
      this.applyScenarioStellcodeStatuses([]);
      this.applyTimerSwitchStellcodeStatuses([]);
      this.applyFallbackStellcodeStatuses([]);
      this.pollUploadJobStatus();
    });
  }

  public onRefreshStatusClicked(): void {
    if (!this.routeStation || this.selectedSyncStellcode.id === 0 || this.areSyncChannelActionsDisabled) {
      this.alertService.alert("Bitte zuerst einen Kanal auswählen.", AlertTypes.warning);
      return;
    }

    this.imageService.refreshImageStatus(this.selectedSyncStellcode.id, this.routeStation.samId).subscribe(() => {
      this.alertService.alert("Der Status-Refresh wurde angestoßen.", AlertTypes.info);
      this.loadStellcodeStatus();
    });
  }

  private getImagesForStation(): void {
    if (!this.routeStation || !this.activeProject) { return; }
    this.projectService.getAllRouteStationImagesWithStationId(this.activeProject.id, this.routeStation.id).subscribe(images => {
      this.imageDataService.updateProjectRouteStationImages(images ?? []);
      this.checkJobAndLoadStellcodeStatus();
    });
    this.projectService.getAllUnassignedRouteStationImagesWithStationId(this.activeProject.id, this.routeStation.id).subscribe(images => {
      this.imageDataService.updateUnassignedProjectRouteStationImages(images ?? []);
    });
  }

  private checkJobAndLoadStellcodeStatus(): void {
    if (!this.routeStation || !this.activeProject) { return; }
    this.imageService.getImageUploadJobStatus(this.routeStation.id).subscribe({
      next: (job) => {
        this.isUploadJobActive = this.activeJobStatuses.includes(job.status);
        if (this.isUploadJobActive) {
          this.applyStellcodeStatuses([]);
          this.applyEngineParameterStellcodeStatuses([]);
          this.applyScenarioStellcodeStatuses([]);
          this.applyTimerSwitchStellcodeStatuses([]);
          this.applyFallbackStellcodeStatuses([]);
          this.pollUploadJobStatus();
          return;
        }

        this.loadStellcodeStatus();
      },
      error: () => {
        this.isUploadJobActive = false;
        this.loadStellcodeStatus();
      }
    });
  }

  private pollUploadJobStatus(): void {
    this.clearUploadJobPolling();
    this.uploadJobPollingTimeout = setTimeout(() => {
      if (!this.routeStation) { return; }
      this.imageService.getImageUploadJobStatus(this.routeStation.id).subscribe({
        next: (job: ImageUploadJob) => {
          this.isUploadJobActive = this.activeJobStatuses.includes(job.status);
          if (this.isUploadJobActive) {
            this.pollUploadJobStatus();
            return;
          }

          this.loadStellcodeStatus();
        },
        error: () => {
          this.isUploadJobActive = false;
          this.loadStellcodeStatus();
        }
      });
    }, 2000);
  }

  private loadStellcodeStatus(): void {
    if (!this.routeStation || !this.activeProject) { return; }
    const projectId = this.activeProject.id;
    const stationId = this.routeStation.id;

    this.projectService.getRouteStationAllStellcodeStatus(projectId, stationId).subscribe({
      next: (result: ProjectRouteStationCombinedStellcodeStatus) => {
        this.applyStellcodeStatuses(result.stellcodeStatus ?? []);
        this.applyEngineParameterStellcodeStatuses(result.engineParameter ?? []);
        this.applyScenarioStellcodeStatuses(result.scenario ?? []);
        this.applyTimerSwitchStellcodeStatuses(result.timerSwitch ?? []);
        this.applyFallbackStellcodeStatuses(result.fallback ?? []);
      },
      error: () => {
        this.applyStellcodeStatuses([]);
        this.applyEngineParameterStellcodeStatuses([]);
        this.applyScenarioStellcodeStatuses([]);
        this.applyTimerSwitchStellcodeStatuses([]);
        this.applyFallbackStellcodeStatuses([]);
      }
    });
  }

  private applyStellcodeStatuses(statusItems: ProjectRouteStationStellcodeStatus[]): void {
    const statusByImageAssignmentId = new Map<number, boolean>(statusItems.map(s => [s.projectRouteStationImageId, s.isOk]));
    this.stationImages = this.stationImages.map(image => {
      const status = statusByImageAssignmentId.get(image.projectRouteStationImageId);
      image.stellcodeStatusOk = status;
      return image;
    });
  }

  private applyEngineParameterStellcodeStatuses(statusItems: ProjectRouteStationEngineParameterStellcodeStatus[]): void {
    this.engineParameterStellcodeStatuses = statusItems;
  }

  private applyScenarioStellcodeStatuses(statusItems: ProjectRouteStationEngineParameterStellcodeStatus[]): void {
    this.scenarioStellcodeStatuses = statusItems;
  }

  private applyTimerSwitchStellcodeStatuses(statusItems: ProjectRouteStationEngineParameterStellcodeStatus[]): void {
    this.timerSwitchStellcodeStatuses = statusItems;
  }

  private applyFallbackStellcodeStatuses(statusItems: ProjectRouteStationEngineParameterStellcodeStatus[]): void {
    this.fallbackStellcodeStatuses = statusItems;
  }

  public get enrichedStationImages(): Array<{ image: ProjectImageData; refs: { type: 'EP' | 'SZ' | 'TS' | 'FB'; ok: boolean; labels: string[] }[] }> {
    const buildMap = (statuses: ProjectRouteStationEngineParameterStellcodeStatus[]) => {
      const map = new Map<number, ProjectRouteStationEngineParameterStellcodeStatus[]>();
      for (const s of statuses) {
        const list = map.get(s.stellcode) ?? [];
        list.push(s);
        map.set(s.stellcode, list);
      }
      return map;
    };

    const epMap = buildMap(this.engineParameterStellcodeStatuses);
    const szMap = buildMap(this.scenarioStellcodeStatuses);
    const tsMap = buildMap(this.timerSwitchStellcodeStatuses);
    const fbMap = buildMap(this.fallbackStellcodeStatuses);

    return this.stationImages.map(image => {
      const refs: { type: 'EP' | 'SZ' | 'TS' | 'FB'; ok: boolean; labels: string[] }[] = [];

      const addRef = (type: 'EP' | 'SZ' | 'TS' | 'FB', items: ProjectRouteStationEngineParameterStellcodeStatus[] | undefined) => {
        if (!items?.length) return;
        refs.push({ type, ok: items.every(e => e.isOk), labels: items.map(e => `DE ${e.de} – ${this.translateState(type, e.state)}`) });
      };

      addRef('EP', epMap.get(image.stellcode));
      addRef('SZ', szMap.get(image.stellcode));
      addRef('TS', tsMap.get(image.stellcode));
      addRef('FB', fbMap.get(image.stellcode));

      return { image, refs };
    });
  }

  public trackByStationImage(index: number, item: { image: ProjectImageData }): number {
    return item.image.projectRouteStationImageId || item.image.imageId || index;
  }

  public trackByRefType(index: number, ref: { type: 'EP' | 'SZ' | 'TS' | 'FB' }): string {
    return ref.type;
  }

  public trackByMissingRefGroup(index: number, group: { type: 'EP' | 'SZ' | 'TS' | 'FB' }): string {
    return group.type;
  }

  public trackByStellcode(index: number, stellcode: number): number {
    return stellcode;
  }

  public get missingRefStellcodes(): { stellcode: number; types: ('EP' | 'SZ' | 'TS' | 'FB')[] }[] {
    const assigned = new Set(this.stationImages.map(i => i.stellcode));
    const missing = new Map<number, Set<'EP' | 'SZ' | 'TS' | 'FB'>>();

    const addMissing = (statuses: ProjectRouteStationEngineParameterStellcodeStatus[], type: 'EP' | 'SZ' | 'TS' | 'FB') => {
      for (const s of statuses) {
        if (!assigned.has(s.stellcode)) {
          if (!missing.has(s.stellcode)) missing.set(s.stellcode, new Set());
          missing.get(s.stellcode)!.add(type);
        }
      }
    };

    addMissing(this.engineParameterStellcodeStatuses, 'EP');
    addMissing(this.scenarioStellcodeStatuses, 'SZ');
    addMissing(this.timerSwitchStellcodeStatuses, 'TS');
    addMissing(this.fallbackStellcodeStatuses, 'FB');

    return [...missing.entries()]
      .map(([stellcode, types]) => ({ stellcode, types: [...types] }))
      .sort((a, b) => a.stellcode - b.stellcode);
  }

  public get missingRefStellcodesByType(): { type: 'EP' | 'SZ' | 'TS' | 'FB'; label: string; stellcodes: number[] }[] {
    const assigned = new Set(this.stationImages.map(i => i.stellcode));
    const byType = new Map<'EP' | 'SZ' | 'TS' | 'FB', Set<number>>();

    const addMissing = (statuses: ProjectRouteStationEngineParameterStellcodeStatus[], type: 'EP' | 'SZ' | 'TS' | 'FB') => {
      for (const s of statuses) {
        if (!assigned.has(s.stellcode)) {
          if (!byType.has(type)) byType.set(type, new Set());
          byType.get(type)!.add(s.stellcode);
        }
      }
    };

    addMissing(this.engineParameterStellcodeStatuses, 'EP');
    addMissing(this.scenarioStellcodeStatuses, 'SZ');
    addMissing(this.timerSwitchStellcodeStatuses, 'TS');
    addMissing(this.fallbackStellcodeStatuses, 'FB');

    return (['EP', 'SZ', 'TS', 'FB'] as const)
      .filter(type => byType.has(type))
      .map(type => ({
        type,
        label: this.getRefTypeLabel(type),
        stellcodes: [...byType.get(type)!].sort((a, b) => a - b)
      }));
  }

  private translateState(type: 'EP' | 'SZ' | 'TS' | 'FB', state: string): string {
    if (type !== 'EP') return state;
    switch (state) {
      case 'Danger': return 'zäher Verkehr';
      case 'Free': return 'Fließverkehr';
      case 'Jam': return 'Stau';
      case 'V60': return 'Harmonisierung 60';
      case 'V80': return 'Harmonisierung 80';
      case 'V100': return 'Harmonisierung 100';
      case 'V120': return 'Harmonisierung 120';
      default: return state;
    }
  }

  public getRefTypeIcon(type: 'EP' | 'SZ' | 'TS' | 'FB'): string {
    switch (type) {
      case 'EP': return 'assets/icons/gear-fill.svg';
      case 'TS': return 'assets/icons/stopwatch.svg';
      case 'SZ': return 'assets/icons/play-btn.svg';
      case 'FB': return 'assets/icons/file-image-fill.svg';
    }
  }

  public getRefTypeLabel(type: 'EP' | 'SZ' | 'TS' | 'FB'): string {
    switch (type) {
      case 'EP': return 'EngineParameter';
      case 'TS': return 'TimerSwitch';
      case 'SZ': return 'Szenario';
      case 'FB': return 'Fallback';
    }
  }

  public getImagePath(image: ProjectImageData): string {
    if (!image.path) return 'assets/icons/broken_image.svg';
    if (image.path.startsWith('assets/')) return image.path;
    return this.environment.apiAddress + image.path;
  }

  private clearUploadJobPolling(): void {
    if (!this.uploadJobPollingTimeout) { return; }
    clearTimeout(this.uploadJobPollingTimeout);
    this.uploadJobPollingTimeout = undefined;
  }

  private clearImageRotationIntervals(): void {
    this.imageRotationIntervals.forEach(id => clearInterval(id));
    this.imageRotationIntervals = [];
  }

  public get showSyncChannelSelect(): boolean {
    return this.channelSyncStellcodeSettings.length > 1;
  }

  public get areSyncChannelActionsDisabled(): boolean {
    return this.isUploadJobActive || this.channelSyncStellcodeSettings.length < 1 || this.selectedSyncStellcode.id === 0;
  }

  private ensureSyncChannelSelection(): void {
    if (this.channelSyncStellcodeSettings.length < 1) {
      this.selectedSyncStellcode = { name: 'Kanal', id: 0 };
      this.isSyncStellcodeOpen = false;
      return;
    }

    if (this.channelSyncStellcodeSettings.length <= 1) {
      this.isSyncStellcodeOpen = false;
    }

    const selectedChannelStillAvailable = this.channelSyncStellcodeSettings.some(channel => channel.id === this.selectedSyncStellcode.id);
    if (selectedChannelStillAvailable) { return; }

    this.selectedSyncStellcode = this.channelSyncStellcodeSettings[0];
  }
}
